import { useLayout } from "@/hooks";
import { useAppSelector } from "@/redux/hooks";
import { activeOperatorSelector } from "@/redux/slices/operator/selectors";
import { AnnouncementBar } from "./AnnouncementBar";
import { ANNOUNCEMENT_MESSAGES } from "./announcementMessages";

/**
 * Client Portal banner for OPERATORS (DEV-1486).
 *
 * Two messages, one replacing the other, driven by a single phase so both can never show at once:
 * the release is coming, then it has landed, then nothing.
 *
 * The client-facing halves live on the legacy PHP pages (before the swap) and in the new client
 * portal (see ClientPortalClientBanner). All three read the same phase from the same config key, so
 * one switch moves every surface at once.
 *
 * Left padding tracks the sidebar the same way VehicleMigrationBanner does, so the message stays
 * centred on the content rather than sliding under the nav when it collapses.
 */
export const ClientPortalAnnouncementBanner = () => {
  /*
   * Read the operator off the selector, NOT `useOperator()` — that hook THROWS when there is no
   * active operator, and this component is the first thing `Private` renders, above every other
   * banner and outside any error boundary.
   *
   * There is a real window where the operator is missing: AccountProvider only shows its loader on
   * the INITIAL mount, so during a later refetch — an FA admin switching between operators, for
   * instance — the tree keeps rendering while `activeOperator` is briefly undefined. A throw there
   * takes down the whole dashboard.
   *
   * An announcement must never be able to do that. Same rule as the PHP half: fail to nothing.
   */
  const announcement = useAppSelector(activeOperatorSelector)?.announcement;
  const {
    layout: { sidebarOpen },
  } = useLayout();

  const phase = announcement?.clientPortalPhase;
  if (!phase) return null;

  const { lead, body } = ANNOUNCEMENT_MESSAGES.operator[phase];

  return (
    <AnnouncementBar
      lead={lead}
      body={body}
      url={announcement.clientPortalUrl}
      innerClassName={sidebarOpen ? "lg:pl-[250px]" : "lg:pl-[65px]"}
    />
  );
};
